// frontend/src/pages/reportpage/ExpensesTab.jsx
import React from 'react';
import {
  exportToCSV,
  formatActor,
  formatDateTime,
  buildHistoryLines,
  paymentDetailsLabel,
  needsPaymentDetails,
} from './reportUtils';

const ExpensesTab = ({
  expenseFilterMode,
  setExpenseFilterMode,
  expenseMonthInput,
  setExpenseMonthInput,
  expenseYearInput,
  setExpenseYearInput,
  expensesReport,
  setExpensesReport,
  fetchExpensesReport,
  isLoading,
  expenseForm,
  setExpenseForm,
  resetExpenseForm,
  isSavingExpense,
  saveExpense,
  startEditExpense,
  deleteExpense,
  openHistory,
  historyModalOpen,
  setHistoryModalOpen,
  historyLoading,
  historyExpenseId,
  setHistoryExpenseId,
  historyLogs,
  setHistoryLogs,
}) => {
  return (
    <div className="expenses-tab">
      {/* Filter Controls */}
      <div className="form-grid" style={{ alignItems: 'end', gridTemplateColumns: 'auto auto 1fr auto auto' }}>
        <div className="form-group">
          <label>Filter By</label>
          <select
            value={expenseFilterMode}
            onChange={(e) => {
              setExpenseFilterMode(e.target.value);
              setExpensesReport(null); // Clear current results
            }}
          >
            <option value="month">Specific Month</option>
            <option value="year">Entire Year</option>
            <option value="all">All Records</option>
          </select>
        </div>

        {expenseFilterMode === 'month' && (
          <div className="form-group">
            <label>Month</label>
            <input
              type="month"
              value={expenseMonthInput}
              onChange={(e) => setExpenseMonthInput(e.target.value)}
            />
          </div>
        )}

        {expenseFilterMode === 'year' && (
          <div className="form-group">
            <label>Year</label>
            <input
              type="number"
              min="2000"
              max="2100"
              value={expenseYearInput}
              onChange={(e) => setExpenseYearInput(e.target.value)}
              placeholder="YYYY"
            />
          </div>
        )}

        <div className="form-group form-group-action">
          <button
            className="btn btn-primary"
            onClick={fetchExpensesReport}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Load Expenses'}
          </button>
        </div>
        
        {expensesReport?.expenses?.length ? (
          <div className="form-group form-group-action">
            <button
              className="btn btn-outline"
              onClick={() => {
                const dataToExport = expensesReport.expenses.map((e) => ({
                  Date: e.date,
                  Category: e.category,
                  'Paid To': e.paidTo,
                  Description: e.description,
                  'Payment Method': e.paymentMethod,
                  'Payment Details': e.paymentDetails,
                  Amount: e.amount,
                  Reference: e.reference || '',
                  Notes: e.notes || '',
                  'Last Edited By': formatActor(e.updatedBy),
                  'Last Edited At': formatDateTime(e.updatedAt),
                }));
                
                // Add summary row
                dataToExport.push({
                  Date: '',
                  Category: '',
                  'Paid To': '',
                  Description: 'TOTAL',
                  'Payment Method': '',
                  'Payment Details': '',
                  Amount: expensesReport.summary.totalExpenses,
                  Reference: '',
                  Notes: '',
                  'Last Edited By': '',
                  'Last Edited At': '',
                });
                
                const filterDesc = expenseFilterMode === 'month' ? expenseMonthInput :
                                  expenseFilterMode === 'year' ? expenseYearInput :
                                  'all-records';
                exportToCSV(dataToExport, `expenses-${filterDesc}.csv`);
              }}
            >
              ⤓ Export Excel
            </button>
          </div>
        ) : null}
      </div>

      {/* Summary Section */}
      {expensesReport?.summary && (
        <div className="compact-summary" style={{ marginTop: 12 }}>
          <div className="compact-summary-item highlight">
            <span className="compact-label">
              {expenseFilterMode === 'month' ? 'Month Total:' : 
               expenseFilterMode === 'year' ? 'Year Total:' : 
               'Total Expenses:'}
            </span>
            <span className="compact-value text-danger">
              KES {Number(expensesReport.summary.totalExpenses || 0).toLocaleString()}
            </span>
          </div>
          {expensesReport.summary.yearTotal && expenseFilterMode === 'month' && (
            <div className="compact-summary-item">
              <span className="compact-label">Year Total ({expenseMonthInput.split('-')[0]}):</span>
              <span className="compact-value">
                KES {Number(expensesReport.summary.yearTotal || 0).toLocaleString()}
              </span>
            </div>
          )}
          <div className="compact-summary-item">
            <span className="compact-label">Top Category:</span>
            <span className="compact-value">{expensesReport.summary.topCategory || '-'}</span>
          </div>
        </div>
      )}

      {/* Add Expense Form */}
      <div className="report-content" style={{ marginTop: 12 }}>
        <div className="report-title-row">
          <h3>{expenseForm.id ? 'Edit Expense' : 'Add Expense'}</h3>
          <div className="btn-group">
            {expenseForm.id ? (
              <button className="btn btn-outline btn-sm" onClick={resetExpenseForm} disabled={isSavingExpense}>
                ✖ Cancel Edit
              </button>
            ) : null}
            <button className="btn btn-outline btn-sm" onClick={() => window.print()}>
              🖨️ Print
            </button>
          </div>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={expenseForm.date}
              onChange={(e) => setExpenseForm((p) => ({ ...p, date: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              value={expenseForm.category}
              onChange={(e) => setExpenseForm((p) => ({ ...p, category: e.target.value }))}
            >
              {[
                'Uncategorized',
                'Office & Stationery',
                'Transport',
                'Utilities',
                'Rent',
                'Salaries & Wages',
                'Bank Charges',
                'ICT / Internet',
                'Audit & Legal',
                'Marketing',
                'Repairs & Maintenance',
                'Meetings / Welfare',
                'Other',
              ].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Payment Method</label>
            <select
              value={expenseForm.paymentMethod}
              onChange={(e) => {
                const newMethod = e.target.value;
                setExpenseForm((p) => ({
                  ...p,
                  paymentMethod: newMethod,
                }));
              }}
            >
              {['Bank', 'M-Pesa', 'Cash', 'Cheque', 'Other'].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Amount (KES)</label>
            <input
              type="number"
              value={expenseForm.amount}
              onChange={(e) => setExpenseForm((p) => ({ ...p, amount: e.target.value }))}
              placeholder="e.g. 2500"
              min="0"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label>Paid To (Who?) *</label>
            <input
              type="text"
              value={expenseForm.paidTo}
              onChange={(e) => setExpenseForm((p) => ({ ...p, paidTo: e.target.value }))}
              placeholder="Person / Company paid"
            />
          </div>

          <div className="form-group">
            <label>{paymentDetailsLabel(expenseForm.paymentMethod)}</label>
            <input
              type="text"
              value={expenseForm.paymentDetails}
              onChange={(e) => setExpenseForm((p) => ({ ...p, paymentDetails: e.target.value }))}
              placeholder={
                String(expenseForm.paymentMethod).toLowerCase().includes('mpesa')
                  ? 'e.g. 07xx xxx xxx / Txn Code'
                  : String(expenseForm.paymentMethod).toLowerCase().includes('bank')
                    ? 'e.g. Bank narration / reference'
                    : 'Optional details'
              }
            />
          </div>

          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Description *</label>
            <input
              type="text"
              value={expenseForm.description}
              onChange={(e) => setExpenseForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="What was this expense for?"
            />
          </div>

          <div className="form-group">
            <label>Reference (optional)</label>
            <input
              type="text"
              value={expenseForm.reference}
              onChange={(e) => setExpenseForm((p) => ({ ...p, reference: e.target.value }))}
              placeholder="Receipt No / Invoice No"
            />
          </div>

          <div className="form-group">
            <label>Notes (optional)</label>
            <input
              type="text"
              value={expenseForm.notes}
              onChange={(e) => setExpenseForm((p) => ({ ...p, notes: e.target.value }))}
              placeholder="Any extra details"
            />
          </div>

          <div className="form-group form-group-action" style={{ gridColumn: '1 / -1' }}>
            <button
              className="btn btn-primary"
              onClick={saveExpense}
              disabled={isSavingExpense}
            >
              {isSavingExpense ? 'Saving...' : (expenseForm.id ? 'Save Changes' : 'Add Expense')}
            </button>
          </div>
        </div>
      </div>

      {/* Expenses List */}
      <div className="report-content" style={{ marginTop: 12 }}>
        <div className="report-title-row">
          <h3>Expenses List</h3>
        </div>

        {isLoading ? (
          <div className="loading">Loading expenses...</div>
        ) : expensesReport?.expenses?.length ? (
          <div className="table-container expenses-table-container">
            <table className="data-table expenses-compact">
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Date</th>
                  <th style={{ width: '120px' }}>Category</th>
                  <th style={{ width: '100px' }}>Paid To</th>
                  <th style={{ minWidth: '150px' }}>Description</th>
                  <th style={{ width: '90px' }}>Payment</th>
                  <th style={{ width: '110px' }}>Details</th>
                  <th style={{ textAlign: 'right', width: '100px' }}>Amount</th>
                  <th style={{ width: '130px' }}>Last Edited</th>
                  <th style={{ width: '200px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expensesReport.expenses.map((e) => (
                  <tr key={e.id}>
                    <td style={{ fontSize: '13px' }}>{e.date}</td>
                    <td style={{ fontSize: '13px' }}>{e.category}</td>
                    <td style={{ fontSize: '13px' }}>{e.paidTo || '-'}</td>
                    <td style={{ fontSize: '13px' }}>
                      {e.description}
                      {e.notes ? <div className="table-note">{e.notes}</div> : null}
                      {e.reference ? <div className="table-note">Ref: {e.reference}</div> : null}
                    </td>
                    <td style={{ fontSize: '13px' }}>{e.paymentMethod || '-'}</td>
                    <td style={{ fontSize: '13px' }}>{e.paymentDetails || '-'}</td>
                    <td style={{ textAlign: 'right', fontSize: '13px' }}>
                      <strong>KES {Number(e.amount || 0).toLocaleString()}</strong>
                    </td>
                    <td style={{ fontSize: '12px' }}>
                      <div>{formatActor(e.updatedBy)}</div>
                      <div className="table-note">{formatDateTime(e.updatedAt)}</div>
                    </td>
                    <td>
                      <div className="btn-group" style={{ gap: 4 }}>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => openHistory(e.id)}
                          disabled={isSavingExpense}
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          📋 History
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => startEditExpense(e)}
                          disabled={isSavingExpense}
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => deleteExpense(e.id)}
                          disabled={isSavingExpense}
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <td colSpan="6" style={{ textAlign: 'right', padding: '12px' }}>
                    TOTAL:
                  </td>
                  <td style={{ textAlign: 'right', padding: '12px' }}>
                    <strong style={{ fontSize: '15px' }}>
                      KES {Number(expensesReport.summary.totalExpenses || 0).toLocaleString()}
                    </strong>
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="table-note">No expenses found. {expenseFilterMode === 'month' ? 'Try selecting a different month.' : 'Try changing your filter.'}</p>
        )}
      </div>

      {/* History Modal */}
      {historyModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 9999,
          }}
          onClick={() => {
            setHistoryModalOpen(false);
            setHistoryExpenseId('');
            setHistoryLogs([]);
          }}
        >
          <div
            style={{
              background: '#fff',
              color: '#111',
              width: 'min(900px, 96vw)',
              maxHeight: '85vh',
              overflow: 'auto',
              borderRadius: 12,
              padding: 16,
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <h3 style={{ margin: 0 }}>Expense History</h3>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => {
                  setHistoryModalOpen(false);
                  setHistoryExpenseId('');
                  setHistoryLogs([]);
                }}
              >
                ✖ Close
              </button>
            </div>

            <div className="table-note" style={{ marginTop: 6 }}>
              Expense ID: {historyExpenseId}
            </div>

            {historyLoading ? (
              <div style={{ marginTop: 12 }}>Loading history...</div>
            ) : historyLogs.length ? (
              <div style={{ marginTop: 12 }}>
                {historyLogs.map((h) => (
                  <div
                    key={h.id}
                    style={{
                      border: '1px solid #e6e6e6',
                      borderRadius: 10,
                      padding: 12,
                      marginBottom: 10,
                      background: '#fafafa',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <strong>{h.action}</strong>
                        <span style={{ marginLeft: 10, color: '#555' }}>{formatDateTime(h.createdAt)}</span>
                      </div>
                      <div style={{ color: '#333' }}>
                        By: <strong>{formatActor(h.actor)}</strong>
                      </div>
                    </div>
                    {buildHistoryLines(h).length > 0 && (
                      <ul style={{ marginTop: 10, marginBottom: 0 }}>
                        {buildHistoryLines(h).map((ln, idx) => (
                          <li key={idx}>{ln}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ marginTop: 12 }} className="table-note">No history found.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesTab;
